import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type { IngredientsHelpDataResponse } from "@/features/prepared-items/types/ingredientsHelpData.types";
import type {
  GetItemsByIdsParams,
  PreparedItem,
} from "@/features/prepared-items/types/preparedItems.types";

export const ingredientsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getIngredientsHelpData: builder.query<IngredientsHelpDataResponse, void>({
      query: () => Endpoints.ingredients.getHelpData,
      transformResponse: (
        response: ApiResponse<IngredientsHelpDataResponse>,
      ): IngredientsHelpDataResponse => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to load ingredients help data",
          );
        }
        return response.data ?? [];
      },
      providesTags: ["IngredientsHelpData"],
    }),

    finishPreparedItemSetup: builder.mutation<ApiResponse<unknown>, FormData>({
      query: (formData) => ({
        url: Endpoints.ingredients.setup,
        method: "POST",
        body: formData,
      }),
      transformResponse: (
        response: ApiResponse<unknown>,
      ): ApiResponse<unknown> => {
        if (!response.success) {
          throw new Error(
            response.message || "Failed to submit prepared item setup",
          );
        }
        return response;
      },
      invalidatesTags: [
        "IngredientsHelpData",
        "PreparedItems",
        "PreparedItem",
        "Items",
      ],
    }),

    getItemsByIds: builder.query<PreparedItem[], GetItemsByIdsParams>({
      query: ({ item_ids }) => {
        const queryParams = new URLSearchParams();
        item_ids.forEach((id, index) => {
          queryParams.append(`item_ids[${index}]`, String(id));
        });
        const url = `${Endpoints.ingredients.getItemsByIds}?${queryParams.toString()}`;
        return url;
      },
      transformResponse: (
        response: ApiResponse<PreparedItem[]>,
      ): PreparedItem[] => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load items by IDs");
        }
        return response.data ?? [];
      },
      providesTags: ["PreparedItems"],
    }),
  }),
});


export const {
  useGetIngredientsHelpDataQuery,
  useFinishPreparedItemSetupMutation,
  useGetItemsByIdsQuery,
  useLazyGetItemsByIdsQuery,
} = ingredientsApi;
